import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Button,
  message,
  Row,
  Col,
  Card,
  Divider,
} from 'antd';
import { Payment, PaymentTransaction, StudentCourse, paymentAPI } from '../../services/paymentAPI';
import { UserCompany, Company } from '../../services/companyAPI';
import dayjs from 'dayjs';

const { TextArea } = Input;

type SelectedCompany = UserCompany | Company;

interface PaymentFormProps {
  visible: boolean;
  payment: Payment | null;
  onCancel: () => void;
  onSuccess: () => void;
  selectedCompany: SelectedCompany | null;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  visible,
  payment,
  onCancel,
  onSuccess,
  selectedCompany,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [studentCourses, setStudentCourses] = useState<StudentCourse[]>([]);
  const [selectedStudentCourse, setSelectedStudentCourse] = useState<StudentCourse | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);

  useEffect(() => {
    if (visible && selectedCompany) {
      loadStudentCourses();
      if (payment) {
        // Redaktə rejimi - PaymentTransaction əlavə etmək üçün
        setSelectedPayment(payment);
        form.setFieldsValue({
          payment: payment.id,
          amount: payment.remaining_amount || 0,
          payment_date: dayjs(),
          payment_method: 'nəğd',
        });
      } else {
        // Yeni ödəniş rejimi
        form.resetFields();
        setSelectedPayment(null);
        setSelectedStudentCourse(null);
      }
    }
  }, [visible, payment, selectedCompany, form]);

  const loadStudentCourses = async () => {
    if (!selectedCompany) return;
    
    try {
      const response = await paymentAPI.getStudentCourses({ 
        company: selectedCompany.id,
        is_active: true 
      });
      if (response.success && response.data) {
        setStudentCourses(response.data);
      }
    } catch (error) {
      console.error('Tələbə kursları yüklənmədi:', error);
    }
  };

  const loadPaymentsForStudentCourse = async (studentCourseId: number) => {
    try {
      const response = await paymentAPI.getPayments({ 
        company: selectedCompany?.id 
      });
      if (response.success && response.data) {
        const filteredPayments = response.data.filter(p => p.student_course === studentCourseId);
        setPayments(filteredPayments);
      }
    } catch (error) {
      console.error('Ödənişlər yüklənmədi:', error);
    }
  };

  const handleStudentCourseChange = (studentCourseId: number) => {
    const studentCourse = studentCourses.find(sc => sc.id === studentCourseId);
    setSelectedStudentCourse(studentCourse || null);
    
    if (studentCourse) {
      loadPaymentsForStudentCourse(studentCourseId);
    }
  };

  const handlePaymentChange = (paymentId: number) => {
    const payment = payments.find(p => p.id === paymentId);
    setSelectedPayment(payment || null);
    
    if (payment) {
      form.setFieldValue('amount', payment.remaining_amount || 0);
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const transactionData: PaymentTransaction = {
        payment: values.payment,
        amount: values.amount,
        payment_date: values.payment_date.format('YYYY-MM-DD'),
        payment_method: values.payment_method,
        receipt_number: values.receipt_number,
        description: values.description,
      };

      const response = await paymentAPI.createPaymentTransaction(transactionData);

      if (response.success) {
        message.success(response.message || 'Ödəniş uğurla əlavə edildi');
        onSuccess();
      } else {
        if (response.errors) {
          // Form xətalarını göstər
          const formErrors: any = {};
          Object.keys(response.errors).forEach(key => {
            formErrors[key] = {
              value: values[key],
              errors: Array.isArray(response.errors[key]) 
                ? response.errors[key] 
                : [response.errors[key]]
            };
          });
          form.setFields(Object.keys(formErrors).map(key => ({
            name: key,
            errors: formErrors[key].errors
          })));
        } else {
          message.error(response.message || 'Ödəniş əlavə edilmədi');
        }
      }
    } catch (error) {
      message.error('Server xətası');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedStudentCourse(null);
    setSelectedPayment(null);
    onCancel();
  };

  return (
    <Modal
      title={payment ? 'Ödəniş Əlavə Et' : 'Yeni Ödəniş Əlavə Et'}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          payment_method: 'nəğd',
          payment_date: dayjs(),
        }}
      >
        {!payment && (
          <>
            <Form.Item
              name="student_course"
              label="Tələbə və Kurs"
              rules={[{ required: true, message: 'Tələbə və kurs seçin' }]}
            >
              <Select
                placeholder="Tələbə və kurs seçin"
                showSearch
                optionFilterProp="children"
                onChange={handleStudentCourseChange}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={studentCourses.map(sc => ({
                  value: sc.id!,
                  label: `${sc.student_name} - ${sc.course_name}`,
                }))}
              />
            </Form.Item>

            {selectedStudentCourse && payments.length > 0 && (
              <Form.Item
                name="payment"
                label="Ödəniş dövrü"
                rules={[{ required: true, message: 'Ödəniş dövrünü seçin' }]}
              >
                <Select
                  placeholder="Ödəniş dövrünü seçin"
                  onChange={handlePaymentChange}
                  options={payments.map(p => ({
                    value: p.id!,
                    label: `${p.period_display} - ${p.remaining_amount} AZN qalıb`,
                    disabled: p.status === 'tam_ödənilib'
                  }))}
                />
              </Form.Item>
            )}
          </>
        )}

        {selectedPayment && (
          <Card className="mb-4" size="small">
            <Row gutter={16}>
              <Col span={12}>
                <div><strong>Dövr:</strong> {selectedPayment.period_display}</div>
                <div><strong>Tələb olunan:</strong> {selectedPayment.required_amount} AZN</div>
              </Col>
              <Col span={12}>
                <div><strong>Ödənilmiş:</strong> {selectedPayment.paid_amount} AZN</div>
                <div><strong>Qalan:</strong> {selectedPayment.remaining_amount} AZN</div>
              </Col>
            </Row>
            {selectedPayment.debt_amount > 0 && (
              <div className="mt-2">
                <strong className="text-red-600">Borc:</strong> {selectedPayment.debt_amount} AZN
              </div>
            )}
          </Card>
        )}

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="amount"
              label="Ödəniş məbləği (AZN)"
              rules={[
                { required: true, message: 'Məbləği daxil edin' },
                { type: 'number', min: 0.01, message: 'Məbləğ 0-dan böyük olmalıdır' }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={2}
                placeholder="0.00"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="payment_date"
              label="Ödəniş tarixi"
              rules={[{ required: true, message: 'Ödəniş tarixini seçin' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD.MM.YYYY"
                placeholder="Tarix seçin"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="payment_method"
              label="Ödəniş üsulu"
              rules={[{ required: true, message: 'Ödəniş üsulunu seçin' }]}
            >
              <Select
                placeholder="Ödəniş üsulunu seçin"
                options={[
                  { value: 'nəğd', label: 'Nəğd' },
                  { value: 'kart', label: 'Bank kartı' },
                  { value: 'köçürmə', label: 'Bank köçürməsi' },
                  { value: 'online', label: 'Online ödəniş' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="receipt_number"
              label="Qəbz nömrəsi"
            >
              <Input placeholder="Qəbz nömrəsi (isteğe bağlı)" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label="Qeyd"
        >
          <TextArea
            rows={3}
            placeholder="Əlavə qeyd (isteğe bağlı)"
          />
        </Form.Item>

        <Divider />

        <Form.Item className="mb-0 text-right">
          <Button onClick={handleCancel} className="mr-2">
            Ləğv et
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Ödəniş Əlavə Et
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PaymentForm;